import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import muzukurulogo from "../../../assets/muzukurublack.png";

interface HeaderProps {
  navItems: string[];
}

const NavBar = ({ navItems }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hostname = window.location.hostname;

  const showLogin =
    hostname === "staging.muzukuru.com" ||
    hostname === "mms.muzukuru.com" ||
    hostname === "localhost";

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavItemClick = (item: string) => {
    console.log(`Navigating to ${item}`);
    setMobileMenuOpen(false);
  };

  const handleLoginClick = () => {
    window.location.href = "/login";
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: { xs: "70px", md: "95px" },
          backdropFilter: "blur(8px) brightness(100%)",
          WebkitBackdropFilter: "blur(8px)",
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          display: "flex",
          alignItems: "center",
          px: { xs: 2, sm: 4, md: "70px" },
          zIndex: 1200,
        }}
      >
        <Stack
          direction="row"
          sx={{ width: "100%", maxWidth: "1107px", margin: "0 auto" }}
          justifyContent="space-between"
          alignItems="center"
        >
          {/* Logo */}
          <Box
            component="img"
            src={muzukurulogo}
            alt="Muzukuru logo"
            sx={{
              width: { xs: "140px", sm: "160px", md: "161px" },
              height: "auto",
            }}
          />

          {/* Desktop Navigation */}
          <Stack
            direction="row"
            spacing={{ sm: 3, md: 5 }}
            alignItems="center"
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {navItems.map((item) => (
              <Typography
                key={item}
                onClick={() => handleNavItemClick(item)}
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "text.primary",
                  cursor: "pointer",
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "#5D4037",
                  },
                }}
              >
                {item}
              </Typography>
            ))}

            {showLogin && (
              <Typography
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "text.primary",
                  cursor: "pointer",
                  border: "1px solid #000000",
                  px: 2,
                  py: 0.5,
                  borderRadius: "20px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#5D4037",
                    color: "#fff",
                    borderColor: "#5D4037",
                  },
                }}
                onClick={handleLoginClick}
              >
                Login
              </Typography>
            )}
          </Stack>

          {/* Mobile Hamburger Menu */}
          <IconButton
            sx={{
              display: { xs: "flex", md: "none" },
              color: "text.primary",
            }}
            onClick={handleMobileMenuToggle}
            aria-label="menu"
          >
            <MenuIcon sx={{ fontSize: 36 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: { xs: "75%", sm: "300px" },
            maxWidth: "300px",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(8px)",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Close Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <IconButton onClick={handleMobileMenuToggle} aria-label="close menu">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Mobile Navigation Items */}
          <List>
            {navItems.map((item) => (
              <ListItem
                key={item}
                onClick={() => handleNavItemClick(item)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(93, 64, 55, 0.1)",
                  },
                }}
              >
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    fontFamily: "Mulish",
                    fontWeight: 700,
                    fontSize: "18px",
                    color: "black",
                  }}
                />
              </ListItem>
            ))}

            {/* Mobile Login Button */}
            {showLogin && (
              <ListItem sx={{ mt: 2 }}>
                <Box
                  onClick={handleLoginClick}
                  sx={{
                    width: "100%",
                    textAlign: "center",
                    fontFamily: "Mulish",
                    fontWeight: 700,
                    fontSize: "16px",
                    color: "text.primary",
                    cursor: "pointer",
                    border: "1px solid #000000",
                    px: 2,
                    py: 1,
                    borderRadius: "20px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#5D4037",
                      color: "#fff",
                      borderColor: "#5D4037",
                    },
                  }}
                >
                  Login
                </Box>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default NavBar;